import { useEffect, useState } from 'react';
import Icons from '../../../../../assets/index';
import Button from '../../../../../components/Button/Button';
import CustomTextAreaInput from '../../InputFields/CustomTextAreaInput';

interface Option {
  id: number;
  name: string;
  action?: string;
  defaultValue?: string;
}

export interface DropdownFieldData {
  options: Option[];
  helpText: string;
}

interface Errors {
  dropdown?: string;
  emptyField?: string;
}

interface Props {
  onDataChange: (data: Partial<DropdownFieldData>) => void;
  errors?: Errors;
  option?: any[]; // Expected format: [{ name: string, action: string, defaultValue: string }]
  helpText?: string;
}

const DropdownDataType = ({ onDataChange, errors = {}, option, helpText }: Props) => {
  const [formData, setFormData] = useState<DropdownFieldData>({
    options: [],
    helpText: helpText || '',
  });

  const [options, setOptions] = useState<Option[]>([{ id: 1, name: '' }]);
  const [nextId, setNextId] = useState(2);
  const [duplicateError, setDuplicateError] = useState(false);

  // 🟡 Initialize options from `option` prop for edit functionality
  useEffect(() => {
    if (option && Array.isArray(option) && option.length > 0) {
      const formattedOptions: Option[] = option.map((opt, index) => ({
        id: index + 1,
        name: opt.name || '',
        action: opt.action,
        defaultValue: opt.defaultValue,
      }));

      setOptions(formattedOptions);
      setNextId(formattedOptions.length + 1);

      setFormData((prev) => ({
        ...prev,
        options: formattedOptions,
      }));
    }
  }, []);

  // 🔁 Validate options on change
  useEffect(() => {
    const optionNames = options.map((option) => option.name.trim()).filter((name) => name !== '');
    const hasDuplicates = new Set(optionNames).size !== optionNames.length;
    setDuplicateError(hasDuplicates);

    const updated = { ...formData, options };

    onDataChange(updated);
  }, [options]);

  const handleHelpTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const updatedText = e.target.value;
    setFormData((prev) => {
      const updated = { ...prev, helpText: updatedText };
      onDataChange(updated);
      return updated;
    });
  };

  const handleInputChange = (index: number, value: string) => {
    const updatedOptions = options.map((option, i) =>
      i === index ? { ...option, name: value } : option
    );
    setOptions(updatedOptions);

    const allFilled = updatedOptions.every((opt) => opt.name.trim() !== '');
    const hasDupes =
      new Set(updatedOptions.map((opt) => opt.name.trim())).size !==
      updatedOptions.filter((opt) => opt.name.trim() !== '').length;

    setDuplicateError(hasDupes);

    if (allFilled && !hasDupes) {
      onDataChange({
        options: updatedOptions,
      });
    }
  };

  const addOption = () => {
    const newOption: Option = { id: nextId, name: '' };
    setOptions((prev) => [...prev, newOption]);
    setNextId((prev) => prev + 1);
  };

  const removeOption = (id: number) => {
    if (options.length <= 1) return;
    setOptions((prev) => prev.filter((option) => option.id !== id));
  };

  const hasDuplicateName = (option: Option, index: number) => {
    return (
      option.name.trim() !== '' &&
      options.findIndex((item, i) => item.name.trim() === option.name.trim() && index !== i) !== -1
    );
  };

  return (
    <div className="flex flex-col ml-4">
      <div>
        <CustomTextAreaInput
          label="Help Text"
          name="helpText"
          value={formData.helpText}
          onChange={handleHelpTextChange}
          placeholder="Enter the description. i.e - This section provides key points of contact for communication and support."
          description="This will help in guiding the person filling this field."
          rows={12}
        />
      </div>

      <label className="mt-4 font-semibold text-ds-gray-500 block text-xs">
        Options <span className="text-[#DC3545]">*</span>
      </label>

      <div className="flex flex-col">
        {options.map((option, index) => (
          <div key={option.id} className="flex items-center">
            <input
              type="text"
              value={option.name}
              onChange={(e) => handleInputChange(index, e.target.value)}
              className={`h-[40px] w-[340px] border-2 rounded-md px-2 text-ds-gray-500 outline-none placeholder:text-ds-gray-300  text-xs ${
                hasDuplicateName(option, index) ||
                errors.dropdown ||
                (errors.emptyField && option.name.trim() === '')
                  ? 'border-[#DC3545]'
                  : 'border-[#E0E0E0]'
              } mt-2`}
              placeholder="Enter option"
            />

            <button
              onClick={() => removeOption(option.id)}
              type="button"
              className="ml-2 mt-3"
              disabled={options.length <= 1}
            >
              <Icons.trashIcon className={options.length <= 1 ? 'opacity-50' : 'opacity-100'} />
            </button>
          </div>
        ))}
      </div>

      {duplicateError && (
        <div className="text-[#DC3545] text-sm mt-2">Duplicate options are not allowed.</div>
      )}
      {errors?.dropdown && options[0].name.trim() === '' && (
        <p className="text-[#DC3545] text-sm mt-2">{errors.dropdown}</p>
      )}

      {errors?.emptyField && options[0].name.trim() !== '' && (
        <p className="text-[#DC3545] text-sm mt-2">{errors.emptyField}</p>
      )}

      <div className="mt-4">
        <Button onClick={addOption} type="button" startIcon={<Icons.plusIcon />}>
          Add Option
        </Button>
      </div>
    </div>
  );
};

export default DropdownDataType;
